import { GD } from "src/GD";
import { IStatTable } from "./types";
import type { OverviewAnalysisFilters } from "./useOverviewAnalysis";

export type OverviewDataFilters = Omit<OverviewAnalysisFilters, "period_kind"> & { size: number };
type CachedOverview = { expiresAt: number; data: IStatTable[] };

const LIVE_CACHE_MS = 5 * 60_000;
const HISTORICAL_CACHE_MS = 60 * 60_000;
const MAX_CACHE_ENTRIES = 24;
const cache = new Map<string, CachedOverview>();
const pending = new Map<string, Promise<IStatTable[]>>();
let sessionEpoch = 0;
let subscribed = false;
// The existing CRM overview backend shares an in-flight slot across filters.
// Serializing this page's requests prevents a previous-period request from
// receiving another period's response while that backend remains unchanged.
let requestQueue: Promise<unknown> = Promise.resolve();

const today = () => {
	const date = new Date();
	return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
};

/** Session-scoped memory cache shared by charts and their background analysis. */
export const loadOverview = (filters: OverviewDataFilters, userId: number, projectId: number): Promise<IStatTable[]> => {
	if (!subscribed) {
		subscribed = true;
		GD.S_AUTH_LOG_OUT.subscribe(() => {
			sessionEpoch += 1;
			cache.clear();
			pending.clear();
		});
	}
	const { target_id, target_type, from, to, currency, channel_id, instruments, client_ids, size } = filters;
	const key = JSON.stringify([userId, projectId, target_type, target_id, from, to, currency, channel_id, instruments, client_ids, size]);
	const cached = cache.get(key);
	if (cached && cached.expiresAt > Date.now()) return Promise.resolve(cached.data);
	cache.delete(key);
	const existing = pending.get(key);
	if (existing) return existing;
	const epoch = sessionEpoch;

	const request = requestQueue.then(async () => {
		if (epoch !== sessionEpoch) throw new Error("Session changed");
		const contextMatches = async () => {
			const [profile, project] = await Promise.all([GD.REQ_PROFILE.request(), GD.REQ_CURRENT_PROJECT.request()]);
			return epoch === sessionEpoch && profile?.id === userId && project?.id === projectId;
		};
		if (!await contextMatches()) throw new Error("Session changed");
		const result = await GD.REQ_API.request({ method: "overview/get", data: filters, headers: { ignoreBusy: "true" } });
		if (result?.error) throw result.error;
		if (!Array.isArray(result?.data)) throw new Error("Invalid statistics response");
		if (!await contextMatches()) throw new Error("Session changed");
		const data = result.data as IStatTable[];
		cache.set(key, { data, expiresAt: Date.now() + (to >= today() ? LIVE_CACHE_MS : HISTORICAL_CACHE_MS) });
		while (cache.size > MAX_CACHE_ENTRIES) cache.delete(cache.keys().next().value!);
		return data;
	});
	requestQueue = request.catch(() => undefined);
	pending.set(key, request);
	void request.finally(() => {
		if (pending.get(key) === request) pending.delete(key);
	}).catch(() => undefined);
	return request;
};

const ANALYSIS_WIDGETS: Record<string, readonly string[]> = {
	donutChart: ["Conversion"],
	informer: ["Refunds", "Chargebacks"],
	informerMultiple: ["Transactions"],
	hBarChartSections: ["Top countries", "Top channels", "Top banks", "Top emails"],
	hBarChart: ["Top errors"],
	pieChart: ["Top statuses"],
	lineChart: ["Transactions"],
};

/** Local rules receive allowlisted aggregates, including email lookup identities,
 * but not website rankings, chart metadata, or arbitrary dashboard sections. */
export const selectAnalysisWidgets = (tables: IStatTable[]): IStatTable[] => tables.map(table => ({
	grid: 1,
	items: table.items.filter(item =>
		item && (ANALYSIS_WIDGETS[item.type]?.includes(item.title) ||
			(item.type === "informer" && /^(Turnover|Declines), [A-Z]{3}$/.test(item.title))),
	).map(item => ({ size: 1, type: item.type, title: item.title, data: item.data, ...(item.labels ? { labels: item.labels } : {}) })),
})).filter(table => table.items.length > 0);

const parseDay = (value: string) => new Date(`${value}T00:00:00Z`);
const formatDay = (date: Date) => date.toISOString().slice(0, 10);

/** Match elapsed month days; otherwise compare the immediately preceding range. */
export const getPreviousOverviewPeriod = (filters: Pick<OverviewAnalysisFilters, "from" | "to" | "period_kind">) => {
	const from = parseDay(filters.from);
	const to = parseDay(filters.to);
	if (filters.period_kind === "this_month" || filters.period_kind === "last_month") {
		const previousFrom = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth() - 1, 1));
		const previousMonthEnd = new Date(Date.UTC(from.getUTCFullYear(), from.getUTCMonth(), 0));
		const previousTo = filters.period_kind === "last_month"
			? previousMonthEnd
			: new Date(Date.UTC(previousFrom.getUTCFullYear(), previousFrom.getUTCMonth(), Math.min(to.getUTCDate(), previousMonthEnd.getUTCDate())));
		return { from: formatDay(previousFrom), to: formatDay(previousTo) };
	}
	const previousTo = new Date(from);
	previousTo.setUTCDate(previousTo.getUTCDate() - 1);
	const previousFrom = new Date(previousTo.getTime() - (to.getTime() - from.getTime()));
	return { from: formatDay(previousFrom), to: formatDay(previousTo) };
};
